import {
  ColDef,
  ColumnResizedEvent,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  IGetRowsParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useRef } from "react";
import { useDataset } from "../DataSet/DatasetProvider";
import { FetchRowsResult } from "../types";
import { columnTypes } from "./ColumnTypes";
import "./DynamicGrid.scss";
import { fullGridComponents } from "./gridComponents";
import { useProcessedColumnDefs } from "./useProcessedColumnDefs";

const DynamicGrid = () => {
  const gridApiRef = useRef<GridApi<unknown> | null>(null);
  const sortRef = useRef<
    { columnId: string; direction: "asc" | "desc" } | undefined
  >();
  const lastRequestRef = useRef<{
    startRow: number;
    endRow: number;
    sortKey: string;
    timestamp: number;
  } | null>(null);
  const pendingRequestRef = useRef<Promise<FetchRowsResult> | null>(null);

  const {
    columnDefinition,
    fetchRows,
    dataSourceId,
    isLoading,
    hasError,
    traceId,
    settings,
    filtersVersion,
  } = useDataset();

  const createDatasource = useCallback(
    () => ({
      getRows: async (gridParams: IGetRowsParams) => {
        const now = Date.now();
        const sortKey = sortRef.current
          ? `${sortRef.current.columnId}-${sortRef.current.direction}`
          : "none";

        const requestKey = {
          startRow: gridParams.startRow,
          endRow: gridParams.endRow,
          sortKey,
          timestamp: now,
        };

        // Check if this is a duplicate request (same params within 100ms)
        if (lastRequestRef.current && pendingRequestRef.current) {
          const timeSinceLastRequest = now - lastRequestRef.current.timestamp;
          const isSameRequest =
            lastRequestRef.current.startRow === requestKey.startRow &&
            lastRequestRef.current.endRow === requestKey.endRow &&
            lastRequestRef.current.sortKey === requestKey.sortKey;

          if (isSameRequest && timeSinceLastRequest < 100) {
            // Reuse the pending promise result
            try {
              const result = await pendingRequestRef.current;
              if (result.canceled) {
                return;
              }
              const rows = result.rows ?? [];
              const totalRows = result.total;

              let lastRow: number | undefined = undefined;
              if (rows.length < gridParams.endRow - gridParams.startRow) {
                lastRow = gridParams.startRow + rows.length;
              } else if (typeof totalRows === "number") {
                lastRow = totalRows;
              }

              gridParams.successCallback(rows, lastRow);
            } catch {
              gridParams.failCallback();
            }
            return;
          }
        }

        lastRequestRef.current = requestKey;

        const promise = fetchRows({
          startRow: gridParams.startRow,
          endRow: gridParams.endRow,
          sort: sortRef.current,
        });
        pendingRequestRef.current = promise;

        try {
          const result = await promise;
          if (result.canceled) {
            return;
          }
          const rows = result.rows ?? [];
          const totalRows = result.total;

          let lastRow: number | undefined = undefined;
          if (rows.length < gridParams.endRow - gridParams.startRow) {
            lastRow = gridParams.startRow + rows.length;
          } else if (typeof totalRows === "number") {
            lastRow = totalRows;
          }

          gridParams.successCallback(rows, lastRow);
        } catch {
          gridParams.failCallback();
        } finally {
          if (pendingRequestRef.current === promise) {
            pendingRequestRef.current = null;
          }
        }
      },
    }),
    [fetchRows],
  );

  const colDefs = useProcessedColumnDefs(columnDefinition, dataSourceId);

  const onColumnResized = useCallback(
    (params: ColumnResizedEvent) => {
      if (!params.finished) return;
      const widths = params.api
        .getColumnState()
        .map(({ colId, width }) => ({ colId, width }));
      localStorage.setItem(dataSourceId, JSON.stringify(widths));
    },
    [dataSourceId],
  );

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      gridApiRef.current = params.api;
      params.api.setGridOption("datasource", createDatasource());
    },
    [createDatasource],
  );

  const onSortChanged = useCallback(() => {
    if (!gridApiRef.current) return;

    const sortedColumn = gridApiRef.current
      .getColumnState()
      .find((c) => c.sort === "asc" || c.sort === "desc");

    const newSort = sortedColumn
      ? { columnId: sortedColumn.colId!, direction: sortedColumn.sort! }
      : undefined;

    sortRef.current = newSort;
    gridApiRef.current.purgeInfiniteCache();
  }, []);

  return (
    <div className="DynamicGrid ag-theme-alpine">
      {!isLoading && hasError && (
        <div className="errorDisplay">
          An error occurred while loading the grid data. Please refresh the page
          and try again.
          <br />
          {traceId ? `TraceID: ${traceId}` : ""}
        </div>
      )}

      {!hasError && (
        <AgGridReact
          key={filtersVersion}
          suppressMultiSort={true}
          columnDefs={colDefs}
          rowModelType="infinite"
          cacheBlockSize={settings?.grid?.cacheBlockSize}
          maxBlocksInCache={settings?.grid?.maxBlocksInCache}
          rowHeight={settings?.grid?.rowHeight}
          onGridReady={onGridReady}
          onColumnResized={onColumnResized}
          onSortChanged={onSortChanged}
          pagination={false}
          suppressContextMenu={false}
          suppressHorizontalScroll={false}
          suppressServerSideFullWidthLoadingRow={true}
          loading={isLoading && columnDefinition.length === 0}
          columnTypes={columnTypes}
          components={fullGridComponents}
        />
      )}
    </div>
  );
};

export default DynamicGrid;
